import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from '../hooks';
import { cln } from '../lib/utils';

interface Props<T extends { id: string | number }> {
  options: T[];
  show: (data: T) => string;
  value: T[];
  onChange: (value: T[]) => void;
  label?: string;
  error?: string;
  required?: boolean;
}

export function Multiselect<T extends { id: string | number }>(props: Props<T>) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDetailsElement | null>(null);
  const { value, error, label, required, options, onChange, show } = props;
  const id = crypto.randomUUID();
  const emptyListId = useRef<string>(id);

  useOnClickOutside(ref.current, (e) => {
    if (!ref.current?.contains(e.target as any)) ref.current?.removeAttribute('open');
  });

  const handleCheck = (option: T) => () => {
    if (value.some(({ id }) => id === option.id)) onChange(value.filter((o) => o.id !== option.id));
    else onChange([...value, option]);
  };

  return (
    <span className="grid grid-rows-[min-content_auto_min-content] w-full">
      {label && (
        <label htmlFor={id} className={cln(error && '!text-danger-500', 'text-label capitalize')}>
          {t(label)}
          {required && <span className="text-[red]">*</span>}
        </label>
      )}
      <details ref={ref} className="relative">
        <summary
          className={cln(
            error && '!border-danger-500',
            'grid grid-cols-[auto_max-content] grid-rows-1 items-center text-input border border-neutral-800 border-opacity-30 p-2 min-w-[160px] bg-transparent rounded outline-none capitalize hover:border-opacity-100',
          )}
        >
          {!!value.length && <span>{value.map(show).join(', ')}</span>}
          {!value.length && (
            <span className="opacity-60 normal-case">{t('common.multiselectPlaceholder')}</span>
          )}

          <i className="ri-arrow-down-s-line chevron col-start-2 row-start-1 row-span-2 self-center" />
        </summary>
        <div className="max-h-80 overflow-auto">
          <ul className="absolute left-0 right-0 bg-neutral-50 shadow-lg z-10">
            {!options.length && (
              <li
                key={emptyListId.current}
                className="flex items-center gap-2 py-2 px-4 opacity-30 hover:bg-opacity-5"
              >
                {t('common.emptyList')}
              </li>
            )}
            {options.map((o) => (
              <li
                key={o.id}
                className="flex items-center gap-2 py-2 px-4 hover:bg-neutral-900 hover:bg-opacity-5 cursor-pointer"
                onClick={handleCheck(o)}
              >
                <input type="checkbox" checked={value.some(({ id }) => id === o.id)} readOnly />
                {show(o)}
              </li>
            ))}
          </ul>
        </div>
      </details>
      {error && <span className="error">{error}</span>}
    </span>
  );
}
